import { useState, useEffect, useCallback } from 'react'
import { 
  ClientDashboardState, 
  ClientStats, 
  SearchFilters,
  PropertyWithLandlord
} from '@/types/client'
import { getApiUrl } from '@/lib/apiUrl'

interface UseClientDataProps {
  userId: string
}

export function useClientData({ userId }: UseClientDataProps) {
  const [state, setState] = useState<ClientDashboardState>({
    properties: [],
    applications: [],
    payments: [],
    tenancies: [],
    savedProperties: [],
    loading: {
      properties: true,
      applications: true,
      payments: true,
      savedProperties: true,
      tenancies: false,
    },
    error: null,
    stats: {
      totalProperties: 0,
      activeApplications: 0,
      savedProperties: 0,
      completedPayments: 0,
      pendingApplications: 0,
      acceptedApplications: 0,
      rejectedApplications: 0,
    }
  })

  // Fallback listings so the dashboard never feels empty when the API is down
  const getMockProperties = useCallback((): PropertyWithLandlord[] => ([
    {
      _id: 'mock-1',
      id: 'mock-1',
      landlord_id: 'mock-landlord-1',
      title: 'Luxury 3-Bedroom Apartment',
      description: 'Modern finishes, great light, and a city view. Verified landlord.',
      price: 2500000,
      location: 'Victoria Island, Lagos',
      images: ['https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop'],
      bedrooms: 3,
      bathrooms: 3,
      area: 180,
      property_type: 'apartment',
      propertyType: 'apartment',
      rentalType: 'long-term',
      amenities: [],
      is_verified: true,
      is_available: true,
      created_at: new Date().toISOString(),
      landlord: {
        id: 'mock-landlord-1',
        email: 'landlord@example.com',
        role: 'landlord',
        is_verified: true,
        created_at: new Date().toISOString()
      }
    },
    {
      _id: 'mock-2',
      id: 'mock-2',
      landlord_id: 'mock-landlord-2',
      title: 'Cozy 2-Bedroom Bungalow',
      description: 'Quiet neighborhood with a spacious compound. Ready to move in.',
      price: 1800000,
      location: 'Ikeja GRA, Lagos',
      images: ['https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop'],
      bedrooms: 2,
      bathrooms: 2,
      area: 120,
      property_type: 'house',
      propertyType: 'house',
      rentalType: 'long-term',
      amenities: [],
      is_verified: true,
      is_available: true,
      created_at: new Date().toISOString(),
      landlord: {
        id: 'mock-landlord-2',
        email: 'landlord2@example.com',
        role: 'landlord',
        is_verified: true,
        created_at: new Date().toISOString()
      }
    },
    {
      _id: 'mock-3',
      id: 'mock-3',
      landlord_id: 'mock-landlord-3',
      title: 'Executive Penthouse Suite',
      description: 'Panoramic ocean views with rooftop access and premium finishes.',
      price: 5500000,
      location: 'Ikoyi, Lagos',
      images: ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&h=600&fit=crop'],
      bedrooms: 4,
      bathrooms: 4,
      area: 300,
      property_type: 'apartment',
      propertyType: 'apartment',
      rentalType: 'long-term',
      amenities: [],
      is_verified: true,
      is_available: true,
      created_at: new Date().toISOString(),
      landlord: {
        id: 'mock-landlord-3',
        email: 'landlord3@example.com',
        role: 'landlord',
        is_verified: true,
        created_at: new Date().toISOString()
      }
    }
  ]), [])

  // Load properties
  const loadProperties = useCallback(async (filters?: SearchFilters) => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, properties: true }, error: null }))

    try {
      const apiUrl = getApiUrl()

      const queryParams = new URLSearchParams()
      // Always explicitly request verified properties for clients
      queryParams.set('verified', 'true')
      if (filters) {
        if (filters.priceRange[0] > 0 || filters.priceRange[1] < 5001) {
          queryParams.set('minPrice', filters.priceRange[0].toString())
          queryParams.set('maxPrice', filters.priceRange[1].toString())
        }
        if (filters.location) queryParams.set('location', filters.location)
        if (filters.propertyType) queryParams.set('propertyType', filters.propertyType)
        if (filters.rentalType && filters.rentalType !== 'all') queryParams.set('rentalType', filters.rentalType)
        if (filters.duration) queryParams.set('duration', filters.duration)
        if (filters.amenities.length > 0) queryParams.set('amenities', filters.amenities.join(','))
        if (filters.sortBy) queryParams.set('sortBy', filters.sortBy)
      }

      const response = await fetch(`${apiUrl}/properties?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        throw new Error(`Failed to load properties: ${response.statusText}`)
      }

      const data = await response.json()
      // Transform properties to match frontend format
      const propertiesArray = Array.isArray(data)
        ? data
        : Array.isArray((data as any).properties)
          ? (data as any).properties
          : data && typeof data === 'object' && (data as any)._id
            ? [data as any] // wrap a single property object
            : []

      const transformedProperties = propertiesArray.map((prop: any) => ({
        ...prop,
        id: prop._id || prop.id,
        // Extract and normalize image URLs
        images: (() => {
          const imageUrls = prop.images?.map((img: any) => typeof img === 'string' ? img : img.url) || prop.images || [];
          // Normalize localhost URLs to use backend URL from environment
          return imageUrls.map((url: string) => {
            if (!url) return url;
            if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
              const apiUrl = getApiUrl();
              const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
              try {
                const urlObj = new URL(url);
                return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
              } catch {
                return url.replace(/https?:\/\/[^/]+/, apiBaseUrl);
              }
            } else if (url.startsWith('/uploads')) {
              const apiUrl = getApiUrl();
              const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
              return `${apiBaseUrl}${url}`;
            }
            return url;
          });
        })(),
        // Build location from address
        location: prop.location || (prop.address && typeof prop.address === 'object' 
          ? [prop.address.street, prop.address.city, prop.address.state].filter(Boolean).join(', ')
          : 'Location not specified'),
        // Map verification status
        is_verified: prop.isVerified !== undefined ? prop.isVerified : prop.is_verified,
        is_available: prop.isAvailable !== undefined ? prop.isAvailable : prop.is_available,
        created_at: prop.createdAt || prop.created_at,
        // Ensure landlord property exists (required by PropertyWithLandlord)
        landlord: prop.landlord || null
      } as PropertyWithLandlord))

      const finalProperties = transformedProperties.length > 0 ? transformedProperties : getMockProperties()

      setState(prev => ({
        ...prev,
        properties: finalProperties,
        loading: { ...prev.loading, properties: false }
      }))
    } catch (error) {
      console.error('Error loading properties:', error)
      setState(prev => ({
        ...prev,
        properties: getMockProperties(),
        error: error instanceof Error ? error.message : 'Failed to load properties',
        loading: { ...prev.loading, properties: false }
      }))
    }
  }, [userId, getMockProperties])

  // Load applications
  const loadApplications = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, applications: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/applications`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error(`Failed to load applications: ${response.statusText}`)
      }

      const data = await response.json()
      // Transform applications to match frontend format
      const applicationsArray = data.applications || data || []
      const transformedApplications = applicationsArray.map((app: any) => ({
        ...app,
        id: app._id || app.id,
        property_id: app.property?._id || app.property_id || app.property,
        client_id: app.client?._id || app.client_id || app.client,
        created_at: app.createdAt || app.created_at
      }))

      setState(prev => ({
        ...prev,
        applications: transformedApplications,
        loading: { ...prev.loading, applications: false }
      }))
    } catch (error) {
      console.error('Error loading applications:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load applications',
        loading: { ...prev.loading, applications: false }
      }))
    }
  }, [userId])

  // Load payments
  const loadPayments = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, payments: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/payments/history`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error(`Failed to load payments: ${response.statusText}`)
      }

      const data = await response.json()
      // Transform payments to match frontend format
      const paymentsArray = data.payments || data || []
      const transformedPayments = paymentsArray.map((payment: any) => ({
        ...payment,
        id: payment._id || payment.id,
        application_id: payment.application?._id || payment.application_id || payment.application,
        created_at: payment.createdAt || payment.created_at
      }))

      setState(prev => ({
        ...prev,
        payments: transformedPayments,
        loading: { ...prev.loading, payments: false }
      }))
    } catch (error) {
      console.error('Error loading payments:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load payments',
        loading: { ...prev.loading, payments: false }
      }))
    }
  }, [userId])

  // Load saved properties
  const loadSavedProperties = useCallback(async () => {
    if (!userId) return

    setState(prev => ({ ...prev, loading: { ...prev.loading, savedProperties: true }, error: null }))

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/saved-properties`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error(`Failed to load saved properties: ${response.statusText}`)
      }

      const data = await response.json()
      // Transform saved properties to match frontend format
      const savedPropertiesArray = data.savedProperties || data || []
      const transformedSavedProperties = savedPropertiesArray.map((prop: any) => 
        prop._id || prop.id || prop
      )

      setState(prev => ({
        ...prev,
        savedProperties: transformedSavedProperties,
        loading: { ...prev.loading, savedProperties: false }
      }))
    } catch (error) {
      console.error('Error loading saved properties:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load saved properties',
        loading: { ...prev.loading, savedProperties: false }
      }))
    }
  }, [userId])

  const loadTenancies = useCallback(async () => {
    if (!userId) return
    setState(prev => ({ ...prev, loading: { ...prev.loading, tenancies: true }, error: null }))
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setState(prev => ({ ...prev, tenancies: [], loading: { ...prev.loading, tenancies: false } }))
        return
      }
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/applications/tenancies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Failed to load tenancies')
      const data = await response.json()
      const list = (data.tenancies || []).map((t: any) => ({
        ...t,
        applicationId: t.applicationId || t.application_id
      }))
      setState(prev => ({ ...prev, tenancies: list, loading: { ...prev.loading, tenancies: false } }))
    } catch (error) {
      console.error('Error loading tenancies:', error)
      setState(prev => ({
        ...prev,
        tenancies: [],
        loading: { ...prev.loading, tenancies: false }
      }))
    }
  }, [userId])

  // Calculate stats
  const calculateStats = useCallback(() => {
    const stats: ClientStats = {
      totalProperties: state.properties.length,
      activeApplications: state.applications.filter(app => ['pending', 'approved'].includes(app.status)).length,
      savedProperties: state.savedProperties.length,
      completedPayments: state.payments.filter(payment => payment.status === 'completed').length,
      pendingApplications: state.applications.filter(app => app.status === 'pending').length,
      acceptedApplications: state.applications.filter(app => app.status === 'approved').length,
      rejectedApplications: state.applications.filter(app => app.status === 'rejected').length,
    }

    setState(prev => ({ ...prev, stats }))
  }, [state.properties, state.applications, state.payments, state.savedProperties])

  // Toggle saved property
  const toggleSavedProperty = useCallback(async (propertyId: string) => {
    if (!userId) return

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const isSaved = state.savedProperties.includes(propertyId)
      
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/saved-properties`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          propertyId,
          action: isSaved ? 'unsave' : 'save'
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to ${isSaved ? 'remove' : 'save'} property: ${response.statusText}`)
      }

      if (isSaved) {
        setState(prev => ({
          ...prev,
          savedProperties: prev.savedProperties.filter(id => id !== propertyId)
        }))
      } else {
        setState(prev => ({
          ...prev,
          savedProperties: [...prev.savedProperties, propertyId]
        }))
      }
    } catch (error) {
      console.error('Error toggling saved property:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update saved property'
      }))
    }
  }, [userId, state.savedProperties])

  // Submit application
  const submitApplication = useCallback(async (applicationData: any) => {
    if (!userId) return

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/applications`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...applicationData,
          client_id: userId,
          status: 'pending'
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to submit application: ${response.statusText}`)
      }

      const data = await response.json()

      // Reload applications after successful submission
      await loadApplications()
      return data
    } catch (error) {
      console.error('Error submitting application:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to submit application'
      }))
      throw error
    }
  }, [userId, loadApplications])

  // Load data on mount
  useEffect(() => {
    if (userId) {
      loadProperties()
      loadApplications()
      loadPayments()
      loadSavedProperties()
    }
  }, [userId, loadProperties, loadApplications, loadPayments, loadSavedProperties])

  // Calculate stats when data changes
  useEffect(() => {
    calculateStats()
  }, [calculateStats])

  return {
    // State
    properties: state.properties,
    applications: state.applications,
    payments: state.payments,
    tenancies: state.tenancies,
    savedProperties: state.savedProperties,
    loading: state.loading,
    error: state.error,
    stats: state.stats,

    // Actions
    loadProperties,
    loadApplications,
    loadPayments,
    loadSavedProperties,
    loadTenancies,
    toggleSavedProperty,
    submitApplication,

    // Utilities
    refreshAll: () => {
      loadProperties()
      loadApplications()
      loadPayments()
      loadSavedProperties()
      loadTenancies()
    }
  }
}
